/**
 * CompanyReportTemplate.tsx — Company Search. Covers: public_company_info.
 *
 * The payload shape here has nothing to do with the other templates' broker
 * records — it is a single AI-generated report object:
 *
 *   { ok, report_url, query, generated_at, source,
 *     report: {
 *       identity: { resolved_name, legal_name, aliases[], entity_type,
 *         status, jurisdiction, registration_numbers[], founded_date,
 *         dissolved_date, website, addresses[], confidence,
 *         ambiguity_notes[] },
 *       executive_summary: string,
 *       business: { description, industries[], products_and_services[],
 *         markets[], parent_entities[], subsidiaries[] },
 *       ownership[]: { name, owner_type, ownership_percent, relationship, status },
 *       leadership[]: { name, role, status, start_date, end_date },
 *       legal_proceedings[]: { title, court, jurisdiction, case_number,
 *         parties[], filed_date, status, allegations, outcome },
 *       regulatory_actions[] / sanctions[] / financial_signals[] /
 *         adverse_media[]: { title, date, jurisdiction, status, details }
 *         (all four share this exact shape — one EventList renders them all),
 *       risk_assessment: { level, summary, risk_factors[] },
 *       coverage: { jurisdictions_searched[], languages_searched[],
 *         limitations[], unverified_claims[], overall_confidence },
 *     } }
 *
 * Before this template existed, `public_company_info` had no entry in
 * registry.ts's TYPE_MAP, so it fell through to PersonTemplate — built
 * around `prepareCommonResult()`'s broker-record shape, which finds nothing
 * at all in a report like this. The real content was reachable only by
 * opening RawCard's collapsed JSON at the bottom.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import {
  Chips, Cols, FieldTable, Flag, FlagTone, LinkPreviewCard, NoData, RawCard, ResCard, Sech, sentence,
  ShowMoreButton, Stack, Stats, useShowMore, useTokens, ValueList, ValueRow, Verdict, Warn,
} from '../ui/primitives'
import { hasMapPins, MapCard } from '../ui/MapCard'
import type { TemplateProps } from '../registry'

type RegNumber = { type?: string; value?: string; country?: string }
type Address = { address?: string; type?: string; country?: string }
type Owner = { name?: string; ownerType?: string; percent?: number; relationship?: string; status?: string }
type Leader = { name?: string; role?: string; status?: string }
type LegalCase = {
  title?: string
  court?: string
  jurisdiction?: string
  caseNumber?: string
  parties: string[]
  filedDate?: string
  status?: string
  allegations?: string
  outcome?: string
}
type Event = { title?: string; date?: string; jurisdiction?: string; status?: string; details?: string }

type CompanyReport = {
  ok: boolean
  reportUrl?: string
  generatedAt?: string
  queryCompanyName?: string
  resolvedName?: string
  legalName?: string
  aliases: string[]
  entityType?: string
  status?: string
  jurisdiction?: string
  regNumbers: RegNumber[]
  foundedDate?: string
  dissolvedDate?: string
  website?: string
  addresses: Address[]
  confidence?: string
  ambiguityNotes: string[]
  executiveSummary?: string
  businessDescription?: string
  industries: string[]
  productsAndServices: string[]
  markets: string[]
  parentEntities: string[]
  subsidiaries: string[]
  ownership: Owner[]
  leadership: Leader[]
  legalProceedings: LegalCase[]
  regulatoryActions: Event[]
  sanctions: Event[]
  financialSignals: Event[]
  adverseMedia: Event[]
  riskLevel?: string
  riskSummary?: string
  riskFactors: string[]
  jurisdictionsSearched: string[]
  languagesSearched: string[]
  limitations: string[]
  unverifiedClaims: string[]
  overallConfidence?: string
}

function normalizeEvents(raw: any): Event[] {
  return (Array.isArray(raw) ? raw : []).map((e: any) => ({
    title: e?.title || undefined,
    date: e?.date || undefined,
    jurisdiction: e?.jurisdiction || undefined,
    status: e?.status || undefined,
    details: e?.details || undefined,
  }))
}

function extract(apiData: any): CompanyReport | null {
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  for (const e of entities) {
    const rows: any[] = Array.isArray(e?.data) ? e.data : []
    for (const row of rows) {
      const report = row?.report
      if (!report || typeof report !== 'object') continue

      const identity = report.identity || {}
      const business = report.business || {}
      const risk = report.risk_assessment || {}
      const coverage = report.coverage || {}

      return {
        ok: row.ok !== false,
        reportUrl: row.report_url || undefined,
        generatedAt: row.generated_at || undefined,
        queryCompanyName: row.query?.company_name || undefined,
        resolvedName: identity.resolved_name || undefined,
        legalName: identity.legal_name || undefined,
        aliases: Array.isArray(identity.aliases) ? identity.aliases : [],
        entityType: identity.entity_type || undefined,
        status: identity.status || undefined,
        jurisdiction: identity.jurisdiction || undefined,
        regNumbers: (Array.isArray(identity.registration_numbers) ? identity.registration_numbers : []).map((n: any) => ({
          type: n?.type || undefined,
          value: n?.value || undefined,
          country: n?.country || undefined,
        })),
        foundedDate: identity.founded_date || undefined,
        dissolvedDate: identity.dissolved_date || undefined,
        website: identity.website || undefined,
        addresses: (Array.isArray(identity.addresses) ? identity.addresses : []).map((a: any) => ({
          address: a?.address || undefined,
          type: a?.type || undefined,
          country: a?.country || undefined,
        })),
        confidence: identity.confidence || undefined,
        ambiguityNotes: Array.isArray(identity.ambiguity_notes) ? identity.ambiguity_notes : [],
        executiveSummary: report.executive_summary || undefined,
        businessDescription: business.description || undefined,
        industries: Array.isArray(business.industries) ? business.industries : [],
        productsAndServices: Array.isArray(business.products_and_services) ? business.products_and_services : [],
        markets: Array.isArray(business.markets) ? business.markets : [],
        parentEntities: Array.isArray(business.parent_entities) ? business.parent_entities : [],
        subsidiaries: Array.isArray(business.subsidiaries) ? business.subsidiaries : [],
        ownership: (Array.isArray(report.ownership) ? report.ownership : []).map((o: any) => ({
          name: o?.name || undefined,
          ownerType: o?.owner_type || undefined,
          percent: typeof o?.ownership_percent === 'number' ? o.ownership_percent : undefined,
          relationship: o?.relationship || undefined,
          status: o?.status || undefined,
        })),
        leadership: (Array.isArray(report.leadership) ? report.leadership : []).map((l: any) => ({
          name: l?.name || undefined,
          role: l?.role || undefined,
          status: l?.status || undefined,
        })),
        legalProceedings: (Array.isArray(report.legal_proceedings) ? report.legal_proceedings : []).map((c: any) => ({
          title: c?.title || undefined,
          court: c?.court || undefined,
          jurisdiction: c?.jurisdiction || undefined,
          caseNumber: c?.case_number || undefined,
          parties: Array.isArray(c?.parties) ? c.parties : [],
          filedDate: c?.filed_date || undefined,
          status: c?.status || undefined,
          allegations: c?.allegations || undefined,
          outcome: c?.outcome || undefined,
        })),
        regulatoryActions: normalizeEvents(report.regulatory_actions),
        sanctions: normalizeEvents(report.sanctions),
        financialSignals: normalizeEvents(report.financial_signals),
        adverseMedia: normalizeEvents(report.adverse_media),
        riskLevel: risk.level || undefined,
        riskSummary: risk.summary || undefined,
        riskFactors: Array.isArray(risk.risk_factors) ? risk.risk_factors : [],
        jurisdictionsSearched: Array.isArray(coverage.jurisdictions_searched) ? coverage.jurisdictions_searched : [],
        languagesSearched: Array.isArray(coverage.languages_searched) ? coverage.languages_searched : [],
        limitations: Array.isArray(coverage.limitations) ? coverage.limitations : [],
        unverifiedClaims: Array.isArray(coverage.unverified_claims) ? coverage.unverified_claims : [],
        overallConfidence: coverage.overall_confidence || undefined,
      }
    }
  }
  return null
}

/** DD.MM.YYYY — same format RedditTemplate's formatUtc uses, minus the time
 * (most dates here are date-only, e.g. "1993-09-22"). */
function fmtDate(s?: string): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

function riskTone(level?: string): 'ok' | 'warn' | 'bad' {
  const v = String(level || '').toLowerCase()
  if (v.startsWith('low') || v.startsWith('minimal')) return 'ok'
  if (v.startsWith('high') || v.startsWith('critical') || v.startsWith('severe')) return 'bad'
  return 'warn'
}

/** Status strings on legal/regulatory/sanctions rows are free text from the
 * model ("Resolved", "Under Administrative Appeal", "Pending", ...) — bucket
 * them into a flag colour rather than trying to enumerate every value. */
function eventTone(status?: string): FlagTone {
  const v = String(status || '').toLowerCase()
  if (/resolved|dismissed|settled|closed|good standing/.test(v)) return 'ok'
  if (/pending|appeal|under\s/.test(v)) return 'conflict'
  if (/active|ongoing/.test(v)) return 'variant'
  return 'primary'
}

/** Shared by Regulatory actions / Sanctions / Financial signals / Adverse
 * media — all four sections share the exact same { title, date,
 * jurisdiction, status, details } shape. */
function EventList({ items }: { items: Event[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 5)
  return (
    <>
      {visible.map((e, i) => (
        <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 160 }}>
              {e.title || 'Untitled'}
            </Typography>
            {e.status && <Flag tone={eventTone(e.status)} label={sentence(e.status) as React.ReactNode as string} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[fmtDate(e.date), e.jurisdiction].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {e.details && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '5px', lineHeight: 1.5 }}>{e.details}</Typography>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

function LegalCaseList({ items }: { items: LegalCase[] }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, 4)
  return (
    <>
      {visible.map((c, i) => (
        <Box key={i} sx={{ pt: i ? '12px' : 0, mt: i ? '12px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, flex: 1, minWidth: 200 }}>
              {c.title || 'Untitled case'}
            </Typography>
            {c.status && <Flag tone={eventTone(c.status)} label={sentence(c.status) as React.ReactNode as string} />}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px' }}>
            {[c.court, c.jurisdiction, c.caseNumber, fmtDate(c.filedDate)].filter(Boolean).join(' · ') || ' '}
          </Typography>
          {c.allegations && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '6px', lineHeight: 1.5 }}>
              <Box component="b" sx={{ color: tk.text }}>Allegations: </Box>{c.allegations}
            </Typography>
          )}
          {c.outcome && (
            <Typography sx={{ fontSize: 12, color: tk.text2, mt: '4px', lineHeight: 1.5 }}>
              <Box component="b" sx={{ color: tk.text }}>Outcome: </Box>{c.outcome}
            </Typography>
          )}
          {c.parties.length > 0 && (
            <Box sx={{ mt: '7px' }}>
              <Chips items={c.parties} cap={6} />
            </Box>
          )}
        </Box>
      ))}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

export function CompanyReportTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const r = React.useMemo(() => extract(apiData), [apiData])

  if (!r) {
    return <NoData title="No company report returned" hint="The report generator produced no output for this company." />
  }

  const primaryName = r.resolvedName || r.legalName || r.queryCompanyName || 'Unknown company'
  const statusTone: 'ok' | 'warn' | undefined = /active|good standing/i.test(r.status || '')
    ? 'ok'
    : r.status ? 'warn' : undefined
  const rTone = riskTone(r.riskLevel)

  const addressRows: ValueRow[] = r.addresses.map((a) => ({
    value: a.address || '—',
    note: [a.type, a.country].filter(Boolean).join(' · ') || undefined,
  }))

  const regRows: [string, React.ReactNode][] = r.regNumbers.map((n) => [
    n.type || 'ID',
    [n.value, n.country].filter(Boolean).join(' · ') || '—',
  ])

  const identityRows = ([
    ['Legal name', r.legalName],
    ['Entity type', r.entityType],
    ['Status', r.status],
    ['Jurisdiction', r.jurisdiction],
    ['Founded', fmtDate(r.foundedDate) || undefined],
    ['Dissolved', fmtDate(r.dissolvedDate) || undefined],
    r.website ? ['Website', (
      <Box
        component="a"
        href={r.website}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: '#2D89F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        {r.website.replace(/^https?:\/\//, '')}
      </Box>
    )] : ['Website', undefined],
    ['Confidence', r.confidence ? sentence(r.confidence) : undefined],
  ] as [string, React.ReactNode][]).filter(([, v]) => v)

  const businessHasContent = !!(
    r.businessDescription || r.industries.length || r.productsAndServices.length ||
    r.markets.length || r.parentEntities.length || r.subsidiaries.length
  )

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Company', value: primaryName },
            { label: 'Status', value: r.status ? sentence(r.status) : '—', tone: statusTone },
            { label: 'Entity type', value: r.entityType || '—' },
            { label: 'Jurisdiction', value: r.jurisdiction || '—' },
            { label: 'Founded', value: fmtDate(r.foundedDate) || '—' },
            ...(r.riskLevel
              ? [{ label: 'Risk level', value: sentence(r.riskLevel.split(/[.\s]/)[0]), tone: rTone }]
              : []),
          ]}
        />
      </ResCard>

      {!r.ok && (
        <ResCard>
          <Warn title="This report may be incomplete">
            The report generator flagged this run as unsuccessful — treat the sections below as partial.
          </Warn>
        </ResCard>
      )}

      {r.riskLevel && (
        <ResCard title="Risk assessment" tone={rTone === 'ok' ? 'good' : rTone === 'bad' ? 'bad' : 'accent'}>
          <Verdict
            tone={rTone}
            title={(sentence(r.riskLevel.split(/[.\s]/)[0]) as React.ReactNode as string) || 'Unknown'}
            subtitle="model assessment"
            icon={(
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3 4 6v6c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" />
              </svg>
            )}
          />
          {r.riskSummary && (
            <Typography sx={{ mt: '11px', fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>{r.riskSummary}</Typography>
          )}
          {r.riskFactors.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.riskFactors.length}>Risk factors</Sech>
              <ValueList rows={r.riskFactors.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
        </ResCard>
      )}

      {r.executiveSummary && (
        <ResCard title="Executive summary">
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: tk.text2 }}>{r.executiveSummary}</Typography>
        </ResCard>
      )}

      <Cols n={2}>
        <Stack>
          <ResCard title="Identity">
            <FieldTable rows={identityRows} />
            {r.aliases.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech count={r.aliases.length}>Aliases</Sech>
                <Chips items={r.aliases} />
              </Box>
            )}
          </ResCard>

          {r.regNumbers.length > 0 && (
            <ResCard title="Registration numbers" count={r.regNumbers.length}>
              <FieldTable rows={regRows} />
            </ResCard>
          )}

          {r.ambiguityNotes.length > 0 && (
            <ResCard>
              <Warn title="Entity ambiguity">{r.ambiguityNotes.join(' ')}</Warn>
            </ResCard>
          )}
        </Stack>

        <Stack>
          {hasMapPins([], addressRows) ? (
            <MapCard addressCount={addressRows.length} mapPoints={[]} addresses={addressRows} />
          ) : addressRows.length > 0 ? (
            // A real address on file, just not one `MapCard` could resolve to
            // a country/point (e.g. a bare street address with no country
            // name) — list it as text instead of a map that would only show
            // "No geolocation provided".
            <ResCard title="Addresses" count={addressRows.length}>
              <ValueList rows={addressRows} />
            </ResCard>
          ) : (
            <ResCard title="Addresses">
              <Typography sx={{ fontSize: 12, color: tk.text3 }}>No address on file.</Typography>
            </ResCard>
          )}

          {r.reportUrl && (
            <ResCard title="Full report">
              <LinkPreviewCard
                url={r.reportUrl}
                title="Full company report (PDF)"
                description={r.generatedAt ? `Generated ${fmtDate(r.generatedAt)}` : undefined}
              />
            </ResCard>
          )}
        </Stack>
      </Cols>

      {businessHasContent && (
        <Cols n={2}>
          <Stack>
            <ResCard title="Business">
              {r.businessDescription && (
                <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: tk.text2 }}>{r.businessDescription}</Typography>
              )}
              {r.industries.length > 0 && (
                <Box sx={{ mt: r.businessDescription ? '12px' : 0 }}>
                  <Sech count={r.industries.length}>Industries</Sech>
                  <Chips items={r.industries} tone="b" />
                </Box>
              )}
              {r.markets.length > 0 && (
                <Box sx={{ mt: '12px' }}>
                  <Sech count={r.markets.length}>Markets</Sech>
                  <Chips items={r.markets} />
                </Box>
              )}
              {!r.businessDescription && !r.industries.length && !r.markets.length && (
                <Typography sx={{ fontSize: 12, color: tk.text3 }}>No business description returned.</Typography>
              )}
            </ResCard>
          </Stack>

          <Stack>
            {r.productsAndServices.length > 0 && (
              <ResCard title="Products & services" count={r.productsAndServices.length}>
                <Chips items={r.productsAndServices} cap={12} />
              </ResCard>
            )}
            {(r.parentEntities.length > 0 || r.subsidiaries.length > 0) && (
              <ResCard title="Corporate structure">
                {r.parentEntities.length > 0 && (
                  <Box>
                    <Sech count={r.parentEntities.length}>Parent entities</Sech>
                    <Chips items={r.parentEntities} />
                  </Box>
                )}
                {r.subsidiaries.length > 0 && (
                  <Box sx={{ mt: r.parentEntities.length ? '12px' : 0 }}>
                    <Sech count={r.subsidiaries.length}>Subsidiaries</Sech>
                    <Chips items={r.subsidiaries} cap={10} />
                  </Box>
                )}
              </ResCard>
            )}
          </Stack>
        </Cols>
      )}

      {(r.ownership.length > 0 || r.leadership.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.ownership.length > 0 && (
              <ResCard title="Ownership" count={r.ownership.length}>
                <ValueList
                  rows={r.ownership.map((o) => ({
                    value: o.percent !== undefined ? `${o.name || 'Unknown'} — ${o.percent}%` : o.name || 'Unknown',
                    note: [o.ownerType, o.relationship, o.status].filter(Boolean).join(' · ') || undefined,
                  }))}
                  cap={8}
                />
              </ResCard>
            )}
          </Stack>
          <Stack>
            {r.leadership.length > 0 && (
              <ResCard title="Leadership" count={r.leadership.length}>
                <ValueList
                  rows={r.leadership.map((l) => ({
                    value: l.name || 'Unknown',
                    note: [l.role, l.status].filter(Boolean).join(' · ') || undefined,
                  }))}
                  cap={8}
                />
              </ResCard>
            )}
          </Stack>
        </Cols>
      )}

      {r.legalProceedings.length > 0 && (
        <ResCard title="Legal proceedings" count={r.legalProceedings.length}>
          <LegalCaseList items={r.legalProceedings} />
        </ResCard>
      )}

      {(r.regulatoryActions.length > 0 || r.sanctions.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.regulatoryActions.length > 0 ? (
              <ResCard title="Regulatory actions" count={r.regulatoryActions.length}>
                <EventList items={r.regulatoryActions} />
              </ResCard>
            ) : <Box />}
          </Stack>
          <Stack>
            {r.sanctions.length > 0 ? (
              <ResCard title="Sanctions" count={r.sanctions.length}>
                <EventList items={r.sanctions} />
              </ResCard>
            ) : <Box />}
          </Stack>
        </Cols>
      )}

      {(r.financialSignals.length > 0 || r.adverseMedia.length > 0) && (
        <Cols n={2}>
          <Stack>
            {r.financialSignals.length > 0 ? (
              <ResCard title="Financial signals" count={r.financialSignals.length}>
                <EventList items={r.financialSignals} />
              </ResCard>
            ) : <Box />}
          </Stack>
          <Stack>
            {r.adverseMedia.length > 0 ? (
              <ResCard title="Adverse media" count={r.adverseMedia.length}>
                <EventList items={r.adverseMedia} />
              </ResCard>
            ) : <Box />}
          </Stack>
        </Cols>
      )}

      {(r.jurisdictionsSearched.length > 0 || r.languagesSearched.length > 0 || r.limitations.length > 0 ||
        r.unverifiedClaims.length > 0 || r.overallConfidence) && (
        <ResCard title="Coverage">
          <FieldTable
            rows={([
              ['Jurisdictions searched', r.jurisdictionsSearched.length ? r.jurisdictionsSearched.join(', ') : undefined],
              ['Languages searched', r.languagesSearched.length ? r.languagesSearched.join(', ') : undefined],
              ['Overall confidence', r.overallConfidence ? sentence(r.overallConfidence) : undefined],
            ] as [string, React.ReactNode][]).filter(([, v]) => v)}
          />
          {r.limitations.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.limitations.length}>Limitations</Sech>
              <ValueList rows={r.limitations.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
          {r.unverifiedClaims.length > 0 && (
            <Box sx={{ mt: '12px' }}>
              <Sech count={r.unverifiedClaims.length}>Unverified claims</Sech>
              <ValueList rows={r.unverifiedClaims.map((v) => ({ value: v }))} cap={10} />
            </Box>
          )}
        </ResCard>
      )}

      <RawCard data={apiData} />
    </>
  )
}
